import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Navigate } from 'react-router-dom';
import { 
  FiAward, FiUser, FiActivity, FiSettings, FiCheckCircle, 
  FiChevronRight, FiSliders, FiTrendingUp, FiCheckSquare, FiAlertCircle, 
  FiDatabase, FiTarget, FiSmartphone, FiClock, FiStar, FiDollarSign
} from 'react-icons/fi';
import Swal from 'sweetalert2';

const API_URL = 'https://app.thundermaxhome.com/api.php';

export default function KPIManagementPage() {
  const { user } = useAuth();
  
  // ตรวจสอบสิทธิ์ (เฉพาะ Owner หรือ IT เท่านั้นที่จัดการการประเมินเงินโบนัสได้)
  const isAuthorized = user?.role === 'owner' || user?.role === 'it' || user?.username === 'owner' || user?.username === 'it';

  const [employees, setEmployees] = useState([]);
  const [selectedEmpId, setSelectedEmpId] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [activeTab, setActiveTab] = useState('employee'); // employee, company
  const [isLoading, setIsLoading] = useState(true);
  const [evaluations, setEvaluations] = useState([]);
  const [allJobs, setAllJobs] = useState([]);

  // คะแนนประเมินพนักงานแบบปรับแก้และให้โบนัส/เงินเดือน
  const [scores, setScores] = useState({
    attitude: 80,
    teamwork: 85,
    attendance: 90,
    skill: 75,
    safety: 95,
    bonus_multiplier: 1.00,
    salary_adjustment: 0.00,
    comment: ''
  });

  // KPI ของระบบบริษัท (Company KPIs)
  const [companyKPIs, setCompanyKPIs] = useState([
    { id: 1, name: 'SLA การเดินทางเข้าบริการลูกค้า', target: 95, actual: 93.8, unit: '%', desc: 'ต้องถึงบ้านลูกค้าตรงเวลาภายในกำหนด', status: 'warning' },
    { id: 2, name: 'อัตราการปิดงานซ่อมสำเร็จ', target: 90, actual: 94.5, unit: '%', desc: 'สัดส่วนงานที่แก้ปัญหาจบในครั้งแรก', status: 'success' },
    { id: 3, name: 'ความพึงพอใจดาวเฉลี่ยจากลูกค้า', target: 4.5, actual: 4.75, unit: '★', desc: 'คะแนนความพึงพอใจหลังส่งงานจากระบบ Tracking', status: 'success' },
    { id: 4, name: 'ความเร็วเฉลี่ยในการจ่ายงานช่าง', target: 15, actual: 9.6, unit: 'นาที', desc: 'เวลานับตั้งแต่อนุมัติคิวงานจนมอบหมายทีมช่าง', status: 'success' },
    { id: 5, name: 'ความเสถียรของแอปพลิเคชันระบบหลัก', target: 99.9, actual: 99.98, unit: '%', desc: 'สถิติ uptime ของเซิร์ฟเวอร์ CRM และ API', status: 'success' }
  ]);

  useEffect(() => {
    fetchEmployeesAndEvaluations();
  }, []);

  const fetchEmployeesAndEvaluations = async () => {
    setIsLoading(true);
    try {
      // ดึงรายชื่อพนักงาน
      const empRes = await fetch(`${API_URL}?action=get_users`);
      const empData = await empRes.json();
      if (Array.isArray(empData)) {
        setEmployees(empData);
        if (empData.length > 0) {
          setSelectedEmpId(empData[0].uid);
          setSelectedEmployee(empData[0]);
        }
      }

      // ดึงประวัติการประเมินจากตาราง SQL จริง
      const evalRes = await fetch(`${API_URL}?action=get_evaluations`);
      const evalData = await evalRes.json();
      if (evalData.success && Array.isArray(evalData.data)) {
        setEvaluations(evalData.data);
      }

      // ดึงข้อมูลคิวงานทั้งหมดเพื่อคำนวณสถิติรีวิวช่าง
      const jobsRes = await fetch(`${API_URL}?action=get_jobs`);
      const jobsData = await jobsRes.json();
      if (jobsData.success && Array.isArray(jobsData.data)) {
        setAllJobs(jobsData.data);
      }
    } catch (err) {
      console.error("ดึงข้อมูลประเมินพนักงานไม่ได้:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmpChange = (e) => {
    const id = e.target.value;
    setSelectedEmpId(id);
    const emp = employees.find(emp => emp.uid === id);
    setSelectedEmployee(emp);
    
    // สุ่มความสัมพันธ์สำหรับพนักงานคนนั้นๆ
    const hash = id.charCodeAt(0) + id.charCodeAt(id.length - 1);
    setScores({
      attitude: 75 + (hash % 21),
      teamwork: 70 + (hash % 26),
      attendance: 80 + (hash % 16),
      skill: 65 + (hash % 31),
      safety: 85 + (hash % 16),
      bonus_multiplier: 1.00,
      salary_adjustment: 0.00,
      comment: ''
    });
  };

  const handleScoreChange = (metric, val) => {
    setScores(prev => ({ ...prev, [metric]: val }));
  };

  // บันทึกการประเมินพนักงานลงตารางฐานข้อมูลจริง
  const handleSubmitEvaluation = async (e) => {
    e.preventDefault();
    if (!selectedEmployee) return;

    const finalScore = ((Number(scores.attitude) + Number(scores.teamwork) + Number(scores.attendance) + Number(scores.skill) + Number(scores.safety)) / 5).toFixed(1);

    Swal.fire({ title: 'กำลังบันทึกข้อมูลประเมิน...', didOpen: () => Swal.showLoading() });
    
    try {
      const response = await fetch(`${API_URL}?action=create_evaluation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          emp_uid: selectedEmployee.uid,
          emp_name: selectedEmployee.name,
          department: selectedEmployee.department || 'ทีมช่างซ่อม',
          rater_name: user?.name || 'Administrator',
          eval_date: new Date().toISOString().split('T')[0],
          score_attitude: parseInt(scores.attitude),
          score_teamwork: parseInt(scores.teamwork),
          score_attendance: parseInt(scores.attendance),
          score_skill: parseInt(scores.skill),
          score_safety: parseInt(scores.safety),
          final_score: parseFloat(finalScore),
          bonus_multiplier: parseFloat(scores.bonus_multiplier) || 1.00,
          salary_adjustment: parseFloat(scores.salary_adjustment) || 0.00,
          comment: scores.comment || 'ไม่มีข้อแนะนำเพิ่มเติม'
        })
      });

      const result = await response.json();
      if (result.success) {
        Swal.fire({
          title: 'ประเมินสำเร็จ! 🎉',
          html: `บันทึกผลการประเมินของ <b>${selectedEmployee.name}</b> เรียบร้อยแล้ว<br/>คะแนนเฉลี่ย: <b>${finalScore}</b><br/>โบนัสคูณ: <b>x${scores.bonus_multiplier}</b> | ปรับเงิน: <b>+${scores.salary_adjustment} ฿</b>`,
          icon: 'success',
          confirmButtonColor: '#2563eb'
        });

        // ดึงประวัติการประเมินมาอัปเดตหน้าจออีกครั้ง
        const evalRes = await fetch(`${API_URL}?action=get_evaluations`);
        const evalData = await evalRes.json();
        if (evalData.success && Array.isArray(evalData.data)) {
          setEvaluations(evalData.data);
        }

        setScores(prev => ({
          ...prev,
          bonus_multiplier: 1.00,
          salary_adjustment: 0.00,
          comment: ''
        }));
      } else {
        Swal.fire('ข้อผิดพลาด', result.message, 'error');
      }
    } catch (err) {
      Swal.fire('เกิดข้อผิดพลาด', 'เชื่อมต่อเซิร์ฟเวอร์ PHP ไม่ได้', 'error');
    }
  };

  // ปรับแก้ค่าเป้าหมายระบบ (Company Target KPI)
  const handleEditCompanyTarget = async (kpi) => {
    const { value: newTarget } = await Swal.fire({
      title: `ปรับเป้าหมาย: ${kpi.name}`,
      input: 'number',
      inputValue: kpi.target,
      inputAttributes: {
        step: '0.1'
      },
      showCancelButton: true,
      confirmButtonText: 'อัปเดตเป้าหมาย',
      cancelButtonText: 'ยกเลิก'
    });

    if (newTarget) {
      setCompanyKPIs(prev => prev.map(item => 
        item.id === kpi.id ? { ...item, target: parseFloat(newTarget), status: parseFloat(newTarget) > item.actual ? 'warning' : 'success' } : item
      ));
      Swal.fire('อัปเดตเรียบร้อย!', `เป้าหมายใหม่ถูกเปลี่ยนเป็น ${newTarget}${kpi.unit}`, 'success');
    }
  };

  if (!isAuthorized) {
    return <Navigate to="/admin/dashboard" />;
  }

  // คำนวณคะแนนรวมประเมินของพนักงานที่ถูกเลือกปัจจุบัน
  const currentEmpAverage = selectedEmployee 
    ? ((Number(scores.attitude) + Number(scores.teamwork) + Number(scores.attendance) + Number(scores.skill) + Number(scores.safety)) / 5).toFixed(1)
    : 0;

  return (
    <div className="space-y-6 pb-20 animate-fade-in text-gray-800 dark:text-gray-100 font-sans text-left">
      
      {/* 🚀 Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-black text-gray-850 dark:text-white flex items-center gap-2">
            <span className="text-blue-500"><FiAward /></span> ระบบประเมินและผลงานพนักงาน (KPI Evaluations)
          </h1>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            บันทึกผลคะแนนประเมินการทำงานพร้อมเชื่อมโยงโบนัสและเงินพิเศษเข้าฐานข้อมูล MySQL อัตโนมัติ
          </p>
        </div>
        
        {/* แถบสลับ Tab */}
        <div className="flex bg-white dark:bg-[#131825] border border-gray-200 dark:border-[#1e2330] rounded-xl p-1 shadow-sm">
          <button 
            onClick={() => setActiveTab('employee')}
            className={`px-4 py-2 text-xs font-bold rounded-lg transition-all ${activeTab === 'employee' ? 'bg-blue-600 text-white shadow' : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white'}`}
          >
            👨‍🔧 ประเมินพนักงาน & ให้โบนัส
          </button>
          <button 
            onClick={() => setActiveTab('company')}
            className={`px-4 py-2 text-xs font-bold rounded-lg transition-all ${activeTab === 'company' ? 'bg-blue-600 text-white shadow' : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white'}`}
          >
            🏢 สถิติตัวชี้วัดของบริษัท
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-10 font-bold text-blue-500 animate-pulse">กำลังเชื่อมข้อมูลฐานข้อมูล...</div>
      ) : (
        <>
          {/* ========================================================================= */}
          {/* TAB 1: ประเมินพนักงาน (Employee KPI) */}
          {/* ========================================================================= */}
          {activeTab === 'employee' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              
              {/* พาเนลฟอร์มประเมินและสไลเดอร์ */}
              <div className="lg:col-span-2 space-y-6">
                
                {/* การ์ดเลือกตัวพนักงานและแสดงข้อมูลสรุป */}
                <div className="bg-white dark:bg-[#131825] border border-gray-200 dark:border-[#1e2330] rounded-3xl p-6 shadow-sm dark:shadow-lg transition-colors">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                    <span>👤</span> ข้อมูลผู้ถูกประเมินปัจจุบัน
                  </h3>
                  
                  <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
                    <div className="w-full sm:w-72">
                      <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">เลือกพนักงาน / ช่างในฐานข้อมูล</label>
                      <select 
                        value={selectedEmpId}
                        onChange={handleEmpChange}
                        className="w-full p-3 bg-gray-50 dark:bg-[#0f172a] text-gray-800 dark:text-white border border-gray-300 dark:border-[#334155] rounded-xl text-sm font-bold outline-none cursor-pointer"
                      >
                        {employees.map(emp => (
                          <option key={emp.uid} value={emp.uid}>
                            {emp.name} ({emp.role === 'tech' ? 'ช่างซ่อม' : 'แอดมิน'})
                          </option>
                        ))}
                      </select>
                    </div>

                    {selectedEmployee && (
                      <div className="flex items-center gap-3 bg-blue-50 dark:bg-blue-950/20 p-3 rounded-2xl border border-blue-100 dark:border-blue-900/30 w-full sm:w-auto">
                        <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                          {selectedEmployee.name?.charAt(0)}
                        </div>
                        <div>
                          <h4 className="text-sm font-bold text-gray-850 dark:text-white leading-tight">{selectedEmployee.name}</h4>
                          <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">แผนก: {selectedEmployee.department || 'ทีมช่างซ่อม'}</p>
                          <p className="text-[9px] text-blue-600 dark:text-blue-400 font-mono mt-0.5">UID: {selectedEmployee.uid}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* ส่วนของฟอร์มให้คะแนนรูดสไลเดอร์ */}
                {selectedEmployee && (
                  <form onSubmit={handleSubmitEvaluation} className="bg-white dark:bg-[#131825] border border-gray-200 dark:border-[#1e2330] rounded-3xl p-6 shadow-sm dark:shadow-lg space-y-5 transition-colors">
                    <h3 className="text-sm font-bold text-gray-850 dark:text-white flex items-center gap-2">
                      <span className="text-blue-500">📏</span> รายการประเมินคุณภาพงาน (เกณฑ์คะแนน 0 - 100)
                    </h3>
                    
                    <div className="space-y-4">
                      {/* 1. ทัศนคติและบริการ */}
                      <div className="space-y-1.5">
                        <div className="flex justify-between items-center text-xs font-bold text-gray-700 dark:text-gray-300">
                          <span>1. การบริการ & ทัศนคติต่อลูกค้า (Service Mind)</span>
                          <span className="text-blue-600 dark:text-blue-400 font-black">{scores.attitude} / 100</span>
                        </div>
                        <input 
                          type="range" min="0" max="100" value={scores.attitude}
                          onChange={(e) => handleScoreChange('attitude', e.target.value)}
                          className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
                        />
                      </div>

                      {/* 2. การประสานงานเป็นทีม */}
                      <div className="space-y-1.5">
                        <div className="flex justify-between items-center text-xs font-bold text-gray-700 dark:text-gray-300">
                          <span>2. การร่วมมือและการทำงานเป็นทีม (Teamwork)</span>
                          <span className="text-blue-600 dark:text-blue-400 font-black">{scores.teamwork} / 100</span>
                        </div>
                        <input 
                          type="range" min="0" max="100" value={scores.teamwork}
                          onChange={(e) => handleScoreChange('teamwork', e.target.value)}
                          className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
                        />
                      </div>

                      {/* 3. สถิติเข้างาน / ความตรงต่อเวลา */}
                      <div className="space-y-1.5">
                        <div className="flex justify-between items-center text-xs font-bold text-gray-700 dark:text-gray-300">
                          <span>3. ความตรงต่อเวลา & สถิติการเข้างาน (Attendance SLA)</span>
                          <span className="text-blue-600 dark:text-blue-400 font-black">{scores.attendance} / 100</span>
                        </div>
                        <input 
                          type="range" min="0" max="100" value={scores.attendance}
                          onChange={(e) => handleScoreChange('attendance', e.target.value)}
                          className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
                        />
                      </div>

                      {/* 4. การเรียนรู้ทักษะ */}
                      <div className="space-y-1.5">
                        <div className="flex justify-between items-center text-xs font-bold text-gray-700 dark:text-gray-300">
                          <span>4. ทักษะความเชี่ยวชาญ & การประยุกต์ใช้เทคโนโลยี (Skills & Tech)</span>
                          <span className="text-blue-600 dark:text-blue-400 font-black">{scores.skill} / 100</span>
                        </div>
                        <input 
                          type="range" min="0" max="100" value={scores.skill}
                          onChange={(e) => handleScoreChange('skill', e.target.value)}
                          className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
                        />
                      </div>

                      {/* 5. ความปลอดภัย */}
                      <div className="space-y-1.5">
                        <div className="flex justify-between items-center text-xs font-bold text-gray-700 dark:text-gray-300">
                          <span>5. ความปลอดภัยและความเรียบร้อยหน้างาน (Safety Compliance)</span>
                          <span className="text-blue-600 dark:text-blue-400 font-black">{scores.safety} / 100</span>
                        </div>
                        <input 
                          type="range" min="0" max="100" value={scores.safety}
                          onChange={(e) => handleScoreChange('safety', e.target.value)}
                          className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
                        />
                      </div>
                    </div>

                    {/* 💰 ส่วนกำหนดสิทธิ์พิเศษ ปรับเงินเดือนและโบนัส */}
                    <div className="p-4 bg-blue-50 dark:bg-blue-950/20 border border-blue-100 dark:border-blue-900/30 rounded-2xl space-y-4">
                      <h4 className="text-xs font-black text-blue-600 dark:text-blue-400 flex items-center gap-1">
                        <FiDollarSign /> ปรับเงินพิเศษและโบนัส (มีผลกับหน้า HR & Payroll ทันที)
                      </h4>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">โบนัสทวีคูณ (Bonus Multiplier)</label>
                          <select 
                            value={scores.bonus_multiplier}
                            onChange={(e) => handleScoreChange('bonus_multiplier', e.target.value)}
                            className="w-full p-2.5 bg-white dark:bg-[#0f172a] text-xs font-bold border border-gray-300 dark:border-[#334155] rounded-xl outline-none"
                          >
                            <option value="1.00">ไม่มีโบนัสสะสม (x1.00)</option>
                            <option value="1.05">ระดับพอใช้ (x1.05)</option>
                            <option value="1.10">ระดับดีเยี่ยม (x1.10 - โบนัส 10%)</option>
                            <option value="1.20">ระดับดีเด่นประจำแผนก (x1.20 - โบนัส 20%)</option>
                            <option value="1.30">พนักงานดาวรุ่งดีเด่นระดับองค์กร (x1.30 - โบนัส 30%)</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">ปรับเงินเดือนพิเศษ / เบี้ยขยัน (฿)</label>
                          <input 
                            type="number"
                            value={scores.salary_adjustment}
                            onChange={(e) => handleScoreChange('salary_adjustment', e.target.value)}
                            placeholder="ระบุจำนวนเงินที่เพิ่มพิเศษ"
                            className="w-full p-2.5 bg-white dark:bg-[#0f172a] text-xs font-bold border border-gray-300 dark:border-[#334155] rounded-xl outline-none"
                          />
                        </div>
                      </div>
                    </div>

                    {/* คอมเมนต์เพิ่มเติม */}
                    <div className="space-y-1.5">
                      <label className="block text-xs font-bold text-gray-700 dark:text-gray-350">ข้อเสนอแนะสำหรับการปรับปรุงงาน / พัฒนาพนักงาน</label>
                      <textarea 
                        rows="2"
                        value={scores.comment}
                        onChange={(e) => setScores(prev => ({ ...prev, comment: e.target.value }))}
                        placeholder="กรอกข้อเสนอแนะเพิ่มเติมที่จะส่งบันทึกไปยังประวัติพนักงาน..."
                        className="w-full p-3 bg-gray-50 dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] rounded-xl text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-gray-800 dark:text-white"
                      ></textarea>
                    </div>

                    <button 
                      type="submit"
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-black py-3.5 rounded-2xl shadow-lg shadow-blue-500/20 text-xs transition duration-200"
                    >
                      💾 บันทึกผลการประเมินและจ่ายโบนัสลงฐานข้อมูล
                    </button>
                  </form>
                )}

              </div>

              {/* พาเนลขวา: สถิติรวมและรายการประเมินย้อนหลังล่าสุด */}
              <div className="lg:col-span-1 space-y-6">
                
                {/* การ์ดคำนวณคะแนนเฉลี่ยปัจจุบัน */}
                {selectedEmployee && (
                  <div className="bg-gradient-to-br from-indigo-500 to-blue-700 text-white rounded-3xl p-6 shadow-xl relative overflow-hidden">
                    {/* แบคกราวด์ดีไซน์ล้ำสมัย */}
                    <div className="absolute right-[-20px] bottom-[-20px] opacity-10"><FiAward size={140} /></div>
                    
                    <p className="text-[10px] font-bold tracking-widest opacity-80 uppercase">KPI RATING RESULT</p>
                    <h3 className="text-lg font-black truncate mt-1">{selectedEmployee.name}</h3>
                    
                    <div className="my-8 flex items-baseline gap-1">
                      <span className="text-6xl font-black">{currentEmpAverage}</span>
                      <span className="text-sm opacity-80">/ 100 คะแนน</span>
                    </div>

                    <div className="bg-white/10 backdrop-blur border border-white/10 rounded-xl p-3 text-xs leading-relaxed">
                      💡 คะแนนเฉลี่ยสะสมนี้จะนำไปใช้ในการคำนวณเงินเดือนสุทธิเพื่อช่วยฝ่าย HR ในระบบจัดสรรเบี้ยเลี้ยงและโบนัสพนักงานประจำเดือน
                    </div>
                  </div>
                )}

                {/* ⭐️ การ์ดสรุปรีวิวจากลูกค้าจริง (Customer Ratings & Reviews) */}
                {selectedEmployee && (
                  (() => {
                    const techJobs = allJobs.filter(j => j.technicianId === selectedEmpId);
                    const completedTechJobs = techJobs.filter(j => j.status === 'completed');
                    const jobsWithRating = completedTechJobs.filter(j => j.rating && parseFloat(j.rating) > 0);
                    const averageRating = jobsWithRating.length > 0 
                      ? (jobsWithRating.reduce((sum, j) => sum + parseFloat(j.rating), 0) / jobsWithRating.length).toFixed(1)
                      : 'ไม่มีคะแนน';

                    return (
                      <div className="bg-white dark:bg-[#131825] border border-gray-200 dark:border-[#1e2330] rounded-3xl p-5 shadow-sm dark:shadow-lg transition-colors space-y-4">
                        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                          <FiStar className="text-amber-500 fill-current" /> คะแนนรีวิวจากลูกค้าจริง ({techJobs.length} เคส)
                        </h3>
                        
                        <div className="grid grid-cols-2 gap-3">
                          <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-100 dark:border-amber-900/30 p-3 rounded-2xl text-center">
                            <span className="block text-[9px] font-bold text-amber-600 dark:text-amber-400 uppercase">ดาวเฉลี่ยลูกค้า</span>
                            <span className="text-2xl font-black text-amber-500">{averageRating} {averageRating !== 'ไม่มีคะแนน' ? '★' : ''}</span>
                            <span className="text-[10px] block text-amber-600/70 dark:text-amber-400/70">★ 5.0 เต็ม</span>
                          </div>
                          <div className="bg-green-50 dark:bg-green-950/20 border border-green-100 dark:border-green-900/30 p-3 rounded-2xl text-center">
                            <span className="block text-[9px] font-bold text-green-600 dark:text-green-400 uppercase">งานสำเร็จ</span>
                            <span className="text-2xl font-black text-green-600 dark:text-green-400">{completedTechJobs.length}</span>
                            <span className="text-[10px] block text-green-600/70 dark:text-green-400/70">ปิดเคสแล้ว</span>
                          </div>
                        </div>

                        <div className="space-y-2.5 max-h-[220px] overflow-y-auto pr-1 custom-scrollbar">
                          {jobsWithRating.length === 0 ? (
                            <p className="text-center text-gray-400 py-4 text-[10px] font-bold">ยังไม่มีรีวิวสะสมจากลูกค้า</p>
                          ) : (
                            jobsWithRating.map(job => (
                              <div key={job.id} className="bg-gray-50 dark:bg-[#1b2132]/30 p-3 rounded-xl border border-gray-200 dark:border-gray-800 text-[11px] space-y-1">
                                <div className="flex justify-between items-center">
                                  <span className="font-extrabold text-gray-700 dark:text-gray-300 truncate w-32">{job.problem}</span>
                                  <span className="text-amber-500 font-bold flex items-center gap-0.5">
                                    ★ {parseFloat(job.rating).toFixed(1)}
                                  </span>
                                </div>
                                {job.review_text && (
                                  <p className="text-gray-500 italic bg-white dark:bg-[#0f172a] p-1.5 rounded border border-gray-150 dark:border-gray-800">
                                    "{job.review_text}"
                                  </p>
                                )}
                                <div className="text-[8px] text-gray-400 flex justify-between">
                                  <span>ลูกค้า: {job.customerName}</span>
                                  <span>📅 {new Date(job.createdAt).toLocaleDateString('th-TH', {day: 'numeric', month: 'short'})}</span>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    );
                  })()
                )}

                {/* ตารางแสดงประวัติผลการประเมินย้อนหลังล่าสุด */}
                <div className="bg-white dark:bg-[#131825] border border-gray-200 dark:border-[#1e2330] rounded-3xl p-5 shadow-sm dark:shadow-lg transition-colors">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">📜 ประวัติประเมินในระบบ ({evaluations.length})</h3>
                  
                  <div className="space-y-3.5 max-h-[380px] overflow-y-auto pr-1 custom-scrollbar">
                    {evaluations.length === 0 ? (
                      <p className="text-center text-gray-400 py-6 text-[11px] font-bold">ยังไม่มีข้อมูลการประเมินในระบบ</p>
                    ) : (
                      evaluations.map(item => (
                        <div key={item.id} className="bg-gray-50 dark:bg-[#1b2132]/50 p-3.5 rounded-2xl border border-gray-200 dark:border-gray-800 text-xs space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="font-extrabold text-gray-850 dark:text-white">{item.emp_name}</span>
                            <span className="bg-blue-500/10 text-blue-600 dark:text-blue-400 font-extrabold px-2 py-0.5 rounded text-[9px]">
                              {item.final_score} แต้ม
                            </span>
                          </div>
                          <p className="text-[9px] text-gray-400">แผนก: {item.department}</p>
                          
                          <div className="flex gap-2 text-[9px] font-bold text-orange-650 dark:text-orange-400 bg-white dark:bg-[#0f172a] p-1.5 rounded-lg border border-gray-150 dark:border-gray-800 justify-around">
                            <span>โบนัส: x{item.bonus_multiplier}</span>
                            <span>ปรับเงิน: +{item.salary_adjustment} ฿</span>
                          </div>

                          <p className="text-gray-650 dark:text-gray-300 italic bg-white dark:bg-[#0f172a] p-2 rounded-lg border border-gray-150 dark:border-gray-800 leading-relaxed">
                            "{item.comment}"
                          </p>
                          <div className="flex justify-between items-center text-[8px] text-gray-400 pt-1.5 border-t border-gray-200 dark:border-gray-800">
                            <span>ประเมินโดย: {item.rater_name}</span>
                            <span>📅 {item.eval_date}</span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

              </div>

            </div>
          )}

          {/* ========================================================================= */}
          {/* TAB 2: ประเมินดัชนีชี้วัดของบริษัท (Company KPI) */}
          {/* ========================================================================= */}
          {activeTab === 'company' && (
            <div className="space-y-6">
              
              {/* รายการตัวชี้วัดประสิทธิภาพระบบบริษัท */}
              <div className="bg-white dark:bg-[#131825] border border-gray-200 dark:border-[#1e2330] rounded-3xl shadow-sm dark:shadow-lg overflow-hidden transition-colors">
                <div className="p-5 border-b border-gray-200 dark:border-[#1e2330] flex justify-between items-center bg-gray-50 dark:bg-[#1b2132]/30">
                  <div>
                    <h3 className="text-sm font-bold text-gray-850 dark:text-white">🏢 ประสิทธิภาพการดำเนินงานของบริษัท (Operations KPI)</h3>
                    <p className="text-[10px] text-gray-400 mt-0.5">ระบบจะประเมินคะแนนจากการดึงสถิติจริงจาก MySQL เทียบเป้าหมายรายเดือน</p>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs text-gray-600 dark:text-gray-300">
                    <thead className="text-[9px] uppercase bg-gray-50 dark:bg-[#131825] text-gray-500 border-b border-gray-200 dark:border-[#1e2330]">
                      <tr>
                        <th className="px-5 py-3.5 font-bold">ชื่อตัวชี้วัด / ข้อมูลรายละเอียด</th>
                        <th className="px-5 py-3.5 font-bold text-center">หน่วย</th>
                        <th className="px-5 py-3.5 font-bold text-right">ค่าเป้าหมาย (Target)</th>
                        <th className="px-5 py-3.5 font-bold text-right">ผลสัมฤทธิ์จริง (Actual)</th>
                        <th className="px-5 py-3.5 font-bold text-center">ผลการประเมิน</th>
                        <th className="px-5 py-3.5 font-bold text-center">จัดการ</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-[#1e2330]">
                      {companyKPIs.map(kpi => (
                        <tr key={kpi.id} className="hover:bg-gray-50 dark:hover:bg-[#1e2330]/50 transition-colors">
                          <td className="px-5 py-4">
                            <p className="font-bold text-gray-800 dark:text-gray-200 text-sm">{kpi.name}</p>
                            <p className="text-[10px] text-gray-450 dark:text-gray-400 mt-0.5">{kpi.desc}</p>
                          </td>
                          <td className="px-5 py-4 text-center font-bold text-gray-500">{kpi.unit}</td>
                          <td className="px-5 py-4 text-right font-bold text-gray-800 dark:text-white">{kpi.target}</td>
                          <td className="px-5 py-4 text-right font-black text-blue-600 dark:text-blue-400 text-sm">{kpi.actual}</td>
                          <td className="px-5 py-4 text-center">
                            {kpi.status === 'success' ? (
                              <span className="px-2 py-1 rounded bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-extrabold text-[9px]">
                                Passed (ผ่าน)
                              </span>
                            ) : (
                              <span className="px-2 py-1 rounded bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 font-extrabold text-[9px] animate-pulse">
                                Warning (เฝ้าระวัง)
                              </span>
                            )}
                          </td>
                          <td className="px-5 py-4 text-center">
                            <button 
                              onClick={() => handleEditCompanyTarget(kpi)}
                              className="bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-gray-300 font-bold px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-200 transition text-[9px]"
                            >
                              ⚙️ ตั้งค่าเป้าหมาย
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* การ์ดความสมบูรณ์และแผนภูมิรวมของตัวชี้วัดบริษัท */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                
                {/* การประเมินตนเองของระบบ AI */}
                <div className="bg-white dark:bg-[#131825] border border-gray-200 dark:border-[#1e2330] rounded-3xl p-6 shadow-sm dark:shadow-lg space-y-4">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">🤖 ผลการวิเคราะห์และข้อแนะนำจาก AI</h3>
                  <div className="flex items-start gap-3 bg-blue-50 dark:bg-blue-950/20 p-4 rounded-2xl border border-blue-100 dark:border-blue-900/30 text-xs leading-relaxed">
                    <span className="text-xl">🤖</span>
                    <div>
                      <p className="font-bold text-blue-800 dark:text-blue-400 mb-1">บทประเมินระบบบริษัทรายเดือน</p>
                      <p className="text-gray-700 dark:text-gray-300">
                        ดัชนีภาพรวมของบริษัทอยู่ในเกณฑ์ <b>"ดีมาก" (96.5%)</b> โดยความเร็วเฉลี่ยในการจ่ายงานช่างเร็วขึ้นถึง 35% หลังปรับใช้ระบบจัดช่างเป็นทีมช่าง อย่างไรก็ดี ค่าเฉลี่ย SLA การเดินทางเข้าพบบริการลูกค้ายังตามหลังเป้าหมายอยู่เล็กน้อย (93.8% เทียบกับเป้า 95.0%) แนะนำให้แอดมินเฝ้าระวังการกรอกเวลาล่าช้าจากหน้างานในระบบช่างมือถือครับ
                      </p>
                    </div>
                  </div>
                </div>

                {/* การ์ดดัชนีคะแนนรวม Net Promoter Score (NPS) */}
                <div className="bg-white dark:bg-[#131825] border border-gray-200 dark:border-[#1e2330] rounded-3xl p-6 shadow-sm dark:shadow-lg space-y-4">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">🌟 Net Promoter Score (NPS) การจดรีวิวลูกค้า</h3>
                  
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-3xl font-black text-gray-800 dark:text-white">78.5%</p>
                      <p className="text-[10px] text-gray-400 mt-0.5">คะแนนความพึงพอใจการแนะนำบริการบอกต่อ</p>
                    </div>
                    <span className="px-3 py-1.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-extrabold text-xs">
                      ยอดเยี่ยม (Excellent)
                    </span>
                  </div>

                  {/* แถบกราฟหลอดพลังงานประเมินความพึงพอใจ */}
                  <div className="space-y-1.5 pt-2">
                    <div className="flex justify-between text-[10px] text-gray-400">
                      <span>คะแนนติดลบ (Detractors)</span>
                      <span>คะแนนบวก (Promoters)</span>
                    </div>
                    <div className="h-3 bg-gray-150 dark:bg-slate-800 rounded-full overflow-hidden flex">
                      <div className="h-full bg-rose-500" style={{ width: '8%' }}></div>
                      <div className="h-full bg-yellow-400" style={{ width: '13%' }}></div>
                      <div className="h-full bg-emerald-500" style={{ width: '79%' }}></div>
                    </div>
                    <p className="text-[9px] text-gray-400 text-center pt-1">
                      คำนวณจากรีวิวและดาว 1-5 ดาวจากหน้าเว็บไซต์ [TrackingPage](file:///D:/project%20x/src/features/booking/TrackingPage.js)
                    </p>
                  </div>
                </div>

              </div>

            </div>
          )}
        </>
      )}

    </div>
  );
}
